import * as React from 'react';
import {ConfigProvider, ThemeConfig} from 'antd';
import themeConfig from './theme';
import {useEffect, useState} from "react";
import {useThemeConfig} from "../services/hooks/useThemeConfig";

interface ThemeColorConfig {
    'background-1': string;
    'background-2': string;
    'background-3': string;
    'background-4': string;
    'background-5': string;
    'text-1': string;
    'text-2': string;
    'text-3': string;
    'text-4': string;
    'text-5': string;
    'borderColor': string;
}

// ColorConfig for Light and Dark Themes
const lightThemeColors: ThemeColorConfig = {
    'background-1': '#ffffff',
    'background-2': '#f0f0f0',
    'background-3': '#e0e0e0',
    'background-4': '#d0d0d0',
    'background-5': '#c0c0c0',
    'text-1': '#000000',
    'text-2': '#262626',
    'text-3': '#404040',
    'text-4': '#5a5a5a',
    'text-5': '#737373',
    'borderColor': '#808080'
};

const darkThemeColors: ThemeColorConfig = {
    'background-1': '#000000',
    'background-2': '#262626',
    'background-3': '#404040',
    'background-4': '#5a5a5a',
    'background-5': '#737373',
    'text-1': '#ffffff',
    'text-2': '#f0f0f0',
    'text-3': '#e0e0e0',
    'text-4': '#d0d0d0',
    'text-5': '#c0c0c0',
    'borderColor': '#808080'
};

export default function ThemeRegistry({children}: { children: React.ReactNode }) {
    const {darkThemeConfig, lightThemeConfig} = themeConfig;
    const {currentTheme} = useThemeConfig();
    const [currentThemeConfig, setCurrentThemeConfig] = useState(currentTheme === 'dark' ? darkThemeConfig : lightThemeConfig);

    const applyThemeColors = (themeColors: ThemeColorConfig | { [k: string]: string | number }) => {
        Object.entries(themeColors).forEach(([key, value]) => {
            document.body.style.setProperty('--' + key, value);
        });
    };
    // Define the function to set the theme variables dynamically
    const setThemeVariables = (themeConfig: ThemeConfig): void => {
        const root = document.body;
        const {token} = themeConfig;
        if (!token) return;

        // Loop through the theme tokens and set them as CSS variables
        Object.keys(token).forEach((key) => {
            const value: any = token[key as keyof typeof token];
            if (typeof value === 'string' || typeof value === 'number') {
                // Use camelCase directly as CSS variable names
                const cssVarName = `--${key}`; // No transformation, use camelCase directly
                root.style.setProperty(cssVarName, value?.toLocaleString());
            }
        });
    };

    useEffect(() => {
        const latestThemeConfig = currentTheme === 'dark' ? darkThemeConfig : lightThemeConfig;
        const token:any = latestThemeConfig?.token;
        const {colorBgBase, colorTextBase} = token;
        if (latestThemeConfig.token) {
            document.body.style.setProperty('--background-color', colorBgBase);
            document.body.style.setProperty('--text-color', colorTextBase);
        }
        setCurrentThemeConfig(latestThemeConfig);
        const themeColors = currentTheme === 'light' ? lightThemeColors : darkThemeColors;
        applyThemeColors(themeColors);
        setThemeVariables(latestThemeConfig)
    }, [currentTheme, darkThemeConfig, lightThemeConfig]);

    return (
        <ConfigProvider
            theme={currentThemeConfig}
        >
            {children}
        </ConfigProvider>
    );
}
